import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles
} from "@material-ui/core";
import { Assignment } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PublishSharpIcon from "@material-ui/icons/PublishSharp";
import RemoveIcon from "@material-ui/icons/Remove";
import axios from "axios";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import { bookingApi } from "functions/apis";
import { formatDateTime, token } from "functions/Helper";
import {
  announcementStatus,
  fromUtc,
  isUploadedBanner
} from "./AnnouncementShared";

const useStyles = makeStyles(styles);

const activeOptions = [
  { label: "All", value: "" },
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" }
];

const downloadBanner = id =>
  axios
    .get(
      process.env.REACT_APP_BOOKING_API_URL + "/Announcements/Banner/" + id,
      {
        responseType: "arraybuffer",
        headers: { Authorization: localStorage.getItem("Authorization") }
      }
    )
    .then(response => {
      const disposition =
        response.request.getResponseHeader("Content-Disposition") ?? "";
      const match =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ??
        disposition.match(/filename="?([^";]+)"?/i);
      const filename = match
        ? decodeURIComponent(match[1].trim())
        : "announcement-banner.jpg";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch(error => console.log(error));

const AnnouncementList = () => {
  const classes = useStyles();
  const canWrite =
    token["booking.announcements.write"] === "allowed" ||
    token.is_admin === "True";

  const [filter, setFilter] = useState({ searchString: null, isActive: null });
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 10 });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);

  const signal = useRef(new AbortController());

  useEffect(() => {
    return () => signal.current.abort();
  }, []);

  useEffect(() => {
    setLoading(true);
    bookingApi
      .get("Announcements", {
        params: { filter, pagination },
        signal: signal.current.signal
      })
      .then(result => {
        setData(
          result.records.map(record => ({
            ...record,
            actions: (
              <div className="actions-right">
                <Link to={"/admin/Announcement/Detail/" + record.id}>
                  <Button color="info" className="like">
                    <VisibilityIcon /> VIEW
                  </Button>
                </Link>
                {canWrite && (
                  <Link to={"/admin/Announcement/Edit/" + record.id}>
                    <Button color="info" className="edit">
                      <EditIcon /> EDIT
                    </Button>
                  </Link>
                )}
                {canWrite && (
                  <Link to={"/admin/Announcement/Delete/" + record.id}>
                    <Button color="danger" className="remove">
                      <DeleteIcon /> DELETE
                    </Button>
                  </Link>
                )}
                {canWrite && (
                  <Link to={"/admin/Announcement/UploadBanner/" + record.id}>
                    <Button color="info" className="like">
                      <PublishSharpIcon /> UPLOAD BANNER
                    </Button>
                  </Link>
                )}
              </div>
            ),
            bannerFile: record.banner ? (
              <div className="actions-right">
                {isUploadedBanner(record.banner) && (
                  <Button
                    color="info"
                    className="like"
                    onClick={() => downloadBanner(record.id)}
                  >
                    Download
                  </Button>
                )}
                {canWrite && (
                  <Link to={"/admin/Announcement/RemoveBanner/" + record.id}>
                    <Button color="danger" className="like">
                      <RemoveIcon /> REMOVE BANNER
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div>NO FILE ATTACHED</div>
            )
          }))
        );
        setPages(Math.ceil(result.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(error => {
        if (signal.current.signal.aborted) return;
        console.log(error);
        setLoading(false);
      });
  }, [pagination, filter]);

  const handleSearchStringChanged = e => {
    setFilter({
      ...filter,
      searchString: e.target.value === "" ? null : e.target.value
    });
    setPagination(prev => ({ ...prev, pageIndex: 1 }));
  };

  const handleIsActiveChanged = e => {
    setFilter({
      ...filter,
      isActive: e.target.value === "" ? null : e.target.value === "true"
    });
    setPagination(prev => ({ ...prev, pageIndex: 1 }));
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>ANNOUNCEMENTS</h4>
          </CardHeader>
          <CardBody>
            {canWrite && (
              <Link to={"/admin/Announcement/Create"}>
                <Button color="rose" type="button" value="Create">
                  Create
                </Button>
              </Link>
            )}

            <GridContainer alignItems="center">
              <GridItem xs={12} md={8}>
                <CustomInput
                  labelText="Search title, slug or summary"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleSearchStringChanged,
                    value: filter.searchString ?? ""
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <InputLabel className={classes.selectLabel}>
                    Is Active
                  </InputLabel>
                  <Select
                    MenuProps={{ className: classes.selectMenu }}
                    classes={{ select: classes.select }}
                    value={
                      filter.isActive === null ? "" : String(filter.isActive)
                    }
                    onChange={handleIsActiveChanged}
                  >
                    {activeOptions.map(option => (
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected
                        }}
                        value={option.value}
                        key={option.label}
                      >
                        <b>{option.label}</b>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>

            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) =>
                setPagination({ pageSize: pageSize, pageIndex: page + 1 })
              }
              loading={loading}
              data={data}
              filterable={false}
              sortable={false}
              columns={[
                {
                  id: "title",
                  Header: "Title",
                  accessor: d => d.title
                },
                {
                  id: "slug",
                  Header: "Slug",
                  accessor: d => d.slug
                },
                {
                  id: "publishDate",
                  Header: "Publish Date",
                  accessor: d => formatDateTime(fromUtc(d.publishDate))
                },
                {
                  id: "expiryDate",
                  Header: "Expiry Date",
                  accessor: d =>
                    d.expiryDate
                      ? formatDateTime(fromUtc(d.expiryDate))
                      : "No expiry"
                },
                {
                  id: "status",
                  Header: "Status",
                  accessor: d => announcementStatus(d)
                },
                {
                  Header: "Banner File",
                  accessor: "bannerFile",
                  width: 260
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  width: 330
                }
              ]}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default AnnouncementList;
